import React, { useState, useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails, TourEvent } from '../core/_models'
import { Itinerary } from './GeneralTab/components/Itinerary'
import { RouteWaypointsEditor } from './RouteWaypointsEditor'
import { createTourEvent, updateTourEvent } from '../core/_requests'
import { useQuery } from 'react-query'
import { getAll } from '../../../../services/common_requests'
import { toast } from 'react-toastify'

/** First segment of a Nominatim-style address — useful as a short location title. */
function suggestedTitleFromAddress(displayName: string): string {
  if (!displayName?.trim()) return ''
  const parts = displayName.split(',').map((p) => p.trim()).filter(Boolean)
  return (parts[0] || displayName.trim()).slice(0, 120)
}

// Define coordinates for Tunisia's center and boundaries
const TUNISIA_CENTER = { lat: 34.0, lng: 9.5, zoom: 7 }; // Center of Tunisia with appropriate zoom

// Define coordinates for each location with city name and bounding box for location matching
const LOCATION_COORDINATES = {
  tunis: { 
    lat: 36.8065, 
    lng: 10.1815, 
    zoom: 12,
    name: 'Tunis',
    bounds: [[36.7, 10.1], [36.9, 10.3]] // Approximate bounds for Tunis
  },
  sousse: { 
    lat: 35.8245, 
    lng: 10.6346, 
    zoom: 13,
    name: 'Sousse',
    bounds: [[35.7, 10.5], [35.9, 10.7]] // Approximate bounds for Sousse
  },
  sfax: { 
    lat: 34.7478, 
    lng: 10.7661, 
    zoom: 13,
    name: 'Sfax',
    bounds: [[34.6, 10.65], [34.85, 10.85]] // Approximate bounds for Sfax
  },
  djerba: { 
    lat: 33.8070, 
    lng: 10.8453, 
    zoom: 11,
    name: 'Djerba',
    bounds: [[33.6, 10.7], [33.95, 11.0]] // Approximate bounds for Djerba
  }
};

// Define types for Leaflet objects
interface LeafletMarker {
  setLatLng: (latlng: [number, number]) => LeafletMarker;
  addTo: (map: LeafletMap) => LeafletMarker;
  on: (event: string, handler: (e: any) => void) => void;
  getLatLng: () => { lat: number; lng: number };
  remove: () => void;
  hasLayer: (layer: any) => boolean;
}

interface LeafletMap {
  setView: (center: [number, number], zoom: number) => void;
  setMaxBounds: (bounds: [[number, number], [number, number]]) => void;
  on: (event: string, handler: (e: any) => void) => void;
  getZoom: () => number;
  setZoom: (zoom: number) => void;
  invalidateSize: (options: { animate: boolean }) => void;
  hasLayer: (layer: any) => boolean;
}

interface LocationTabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  isActive: boolean
  selectedLanguage: string
  setSelectedLanguage: (lang: string) => void
}

// Interface for reverse geocoding response
interface NominatimResponse {
  display_name: string;
  address: {
    city?: string;
    town?: string;
    village?: string;
    suburb?: string;
    county?: string;
    state?: string;
  }
}

const LocationTab: React.FC<LocationTabProps> = ({ formik, intl, isActive, selectedLanguage, setSelectedLanguage }) => {
  // State for forms visibility
  const [showItineraryForm, setShowItineraryForm] = useState(false)
  
  // State for new form items
  const [newItinerary, setNewItinerary] = useState<TourEvent>({
    title: '',
    content: '',
    description: '',
    // startTime: '',
    // endTime: '',
    day: '',
    order: 1,
    image: undefined
  })

  // Fetch tour events
  const { data: tourEvents, refetch: refetchTourEvents } = useQuery(
    ['tourEvents', formik.values.id, selectedLanguage],
    () => getAll({ language: selectedLanguage }, '/tour-events/by-tour/' + formik.values.id),
    {
      enabled: !!formik.values.id && isActive,
      onError: (error) => {
        console.error('Error fetching tour events:', error)
        toast.error(intl.formatMessage({ id: 'TOUR.EVENTS.FETCH_ERROR' }))
      }
    }
  )

  // Initialize map with provided values
  const [mapZoom, setMapZoom] = useState(formik.values.location?.mapZoom || 7)
  const [isLoading, setIsLoading] = useState(false)
  const mapRef = useRef<HTMLDivElement>(null)
  const leafletScriptLoaded = useRef<boolean>(false)
  const mapInstance = useRef<LeafletMap | null>(null)
  const markerInstance = useRef<LeafletMarker | null>(null)
  const tileLayersLoaded = useRef<boolean>(false)
  
  // Get coordinates from form or use provided values
  const mapLatitude = Number(formik.values.location?.mapLatitude) || 33.4660329;
  const mapLongitude = Number(formik.values.location?.mapLongitude) || 9.0233679;
  
  // Values to display in the form inputs
  const latitude = Number(formik.values.location?.mapLatitude) || 33.4660329;
  const longitude = Number(formik.values.location?.mapLongitude) || 9.0233679;

  // Add Leaflet script loading
  useEffect(() => {
    if (!window.L && !leafletScriptLoaded.current) {
      leafletScriptLoaded.current = true;
      
      // Load Leaflet CSS
      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      linkElement.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      linkElement.crossOrigin = '';
      document.head.appendChild(linkElement);
      
      // Load Leaflet JS
      const scriptElement = document.createElement('script');
      scriptElement.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      scriptElement.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      scriptElement.crossOrigin = '';
      scriptElement.onload = () => {
        initMap();
        // After map is initialized, update with initial coordinates
        if (mapInstance.current) {
          updateMapView(true);
        }
      };
      document.head.appendChild(scriptElement);
    } else if (window.L && !mapInstance.current) {
      initMap();
      if (mapInstance.current) {
        updateMapView(true);
      }
    }
  }, []);

  // Set initial values
  useEffect(() => {
    // Set initial values if not already set
    if (!formik.values.location?.mapLatitude) {
      formik.setFieldValue('location.mapLatitude', 33.4660329);
    }
    if (!formik.values.location?.mapLongitude) {
      formik.setFieldValue('location.mapLongitude', 9.0233679);
    }
    if (!formik.values.location?.mapZoom) {
      formik.setFieldValue('location.mapZoom', 7);
    }
    if (!formik.values.location?.title) {
      formik.setFieldValue('location.title', 'Douz City Center');
    }
    
    // Fetch the real address for the initial coordinates
    if (!formik.values.location?.realAddress) {
      fetchLocationDetails(33.4660329, 9.0233679);
    }
  }, []);

  // Update map when coordinates change
  useEffect(() => {
    if (mapInstance.current) {
      updateMapView(true);
    }
  }, [formik.values.location?.mapLatitude, formik.values.location?.mapLongitude]);

  // Handle adding or updating an itinerary event
  const handleAddItinerary = async (data: TourEvent) => {
    try {
      if (data.title.trim() && data.content.trim()) {
        const tourId = formik.values.id
        if (!tourId) {
          console.error('Tour ID is required')
          return
        }

        const eventData: TourEvent = {
          ...data,
          tour: String(tourId)
        }

        // If events array is empty, use POST, otherwise use PATCH
        if (!tourEvents || tourEvents.length === 0) {
          const response = await createTourEvent(eventData, selectedLanguage)
          await refetchTourEvents()
        } else {
          // If event has an ID, update it
          if (newItinerary._id) {
            const response = await updateTourEvent(newItinerary._id, eventData, selectedLanguage)
            await refetchTourEvents()
          } else {
            // Create new event
            const response = await createTourEvent(eventData, selectedLanguage)
            await refetchTourEvents()
          }
        }

        // Reset form
        setNewItinerary({
          title: '',
          content: '',
          description: '',
          startTime: '',
          endTime: '',
          day: '',
          order: (tourEvents?.length || 0) + 1,
          image: undefined
        } as TourEvent)
        setShowItineraryForm(false)
      }
    } catch (error) {
      console.error('Error handling itinerary:', error)
      toast.error(intl.formatMessage({ id: 'TOUR.EVENTS.UPDATE_ERROR' }))
    }
  }

  // Separate effect for zoom changes to avoid re-centering
  useEffect(() => {
    if (mapInstance.current) {
      // Only update the zoom level without changing the center
      mapInstance.current.setZoom(mapZoom);
    }
  }, [mapZoom]);

  // Initialize the map
  const initMap = () => {
    if (!mapRef.current || !window.L || mapInstance.current) return;
    
    try {
      // Create map centered on Tunisia
      const map = window.L.map(mapRef.current, {
        minZoom: 2,
        maxZoom: 18,  // Allow higher zoom levels
        zoomControl: true,
        scrollWheelZoom: true,
        dragging: true,
        tap: true
      }) as LeafletMap;
      
      // Multiple tile layer options for reliability
      const openStreetMapLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      });
      
      const cartoDBLayer = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      });
      
      const esriWorldImageryLayer = window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      });
      
      // Add the primary layer to the map
      openStreetMapLayer.addTo(map);

      // Handle tile loading errors - try alternative sources
      openStreetMapLayer.on('tileerror', () => {
        if (!map.hasLayer(cartoDBLayer)) {
          console.log('OpenStreetMap tile error, switching to CartoDB');
          cartoDBLayer.addTo(map);
        }
      });
      
      cartoDBLayer.on('tileerror', () => {
        if (!map.hasLayer(esriWorldImageryLayer)) {
          console.log('CartoDB tile error, switching to ESRI World Imagery');
          esriWorldImageryLayer.addTo(map);
        }
      });
      
      // Force a better initial view setting
      map.setView([Number(mapLatitude), Number(mapLongitude)], mapZoom);
      
      // Make sure the map container is properly sized with multiple attempts
      setTimeout(() => {
        map.invalidateSize({ animate: true });
      }, 100);
      
      setTimeout(() => {
        map.invalidateSize({ animate: true });
      }, 500);
      
      setTimeout(() => {
        map.invalidateSize({ animate: true });
      }, 1000);
      
      // Create marker if coordinates are already set
      if (formik.values.location?.mapLatitude && formik.values.location?.mapLongitude) {
        createMarker(map, Number(formik.values.location?.mapLatitude), Number(formik.values.location?.mapLongitude));
      }
      
      // Handle map click - create or move marker
      map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        if (!markerInstance.current) {
          createMarker(map, lat, lng);
        } else {
          markerInstance.current.setLatLng([lat, lng]);
        }
        updateCoordinates(lat, lng);
        fetchLocationDetails(lat, lng);
      });
      
      // Handle zoom change
      map.on('zoomend', () => {
        const newZoom = map.getZoom();
        setMapZoom(newZoom);
        formik.setFieldValue('location.mapZoom', newZoom);
      });
      
      mapInstance.current = map;
      
      // Set flag for tile layers being loaded
      tileLayersLoaded.current = true;
      
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };
  
  // Create a marker and add it to the map
  const createMarker = (map: LeafletMap, lat: number, lng: number) => {
    try {
      // If a marker already exists, remove it
      if (markerInstance.current) {
        markerInstance.current.remove();
      }
      
      // Create a new marker
      const marker = window.L.marker([lat, lng], { draggable: true }).addTo(map) as LeafletMarker;
      
      // Handle marker drag
      marker.on('dragend', (e: any) => {
        const { lat, lng } = e.target.getLatLng();
        updateCoordinates(lat, lng);
        
        // Get address from coordinates when marker is dragged
        fetchLocationDetails(lat, lng);
      });
      
      markerInstance.current = marker;
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  };
  
  // Update map view when coordinates change
  const updateMapView = (centerOnMarker = true) => {
    if (!mapInstance.current) return;
    
    try {
      // Update marker position if coordinates exist
      if (formik.values.location?.mapLatitude && formik.values.location?.mapLongitude) {
        const lat = Number(formik.values.location?.mapLatitude);
        const lng = Number(formik.values.location?.mapLongitude);
        
        // If we should center the map on the marker
        if (centerOnMarker) {
          // Use setTimeout to ensure the map has time to initialize properly
          setTimeout(() => {
            if (mapInstance.current) {
              mapInstance.current.setView([lat, lng], mapZoom);
              mapInstance.current.invalidateSize({ animate: true });
            }
          }, 200);
        }
        
        // Handle marker update
        if (!markerInstance.current && mapInstance.current) {
          createMarker(mapInstance.current, lat, lng);
        } else if (markerInstance.current) {
          markerInstance.current.setLatLng([lat, lng]);
        }
      } else if (!centerOnMarker && mapInstance.current) {
        // If no coordinates but we still need to update view (e.g., init)
        mapInstance.current.setView(
          [TUNISIA_CENTER.lat, TUNISIA_CENTER.lng],
          TUNISIA_CENTER.zoom
        );
      }
      
      // Remove marker if no coordinates
      if (!formik.values.location?.mapLatitude && !formik.values.location?.mapLongitude && markerInstance.current) {
        markerInstance.current.remove();
        markerInstance.current = null;
      }
    } catch (error) {
      console.error('Error updating map view:', error);
    }
  };
  
  // Update coordinates in formik
  const updateCoordinates = (lat: number, lng: number) => {
    formik.setFieldValue('location.mapLatitude', lat);
    formik.setFieldValue('location.mapLongitude', lng);
    
    // Update the zoom level
    if (mapInstance.current) {
      const currentZoom = mapInstance.current.getZoom();
      setMapZoom(currentZoom);
      formik.setFieldValue('location.mapZoom', currentZoom);
    }
  };
  
  // Fetch location details (address) from coordinates
  const fetchLocationDetails = async (lat: number, lng: number) => {
    try {
      setIsLoading(true);
      
      // Fetch address using OpenStreetMap Nominatim API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en', // Get English results
            'User-Agent': 'Touil-Application' // Required by Nominatim ToS
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Set address if available
        if (data.display_name) {
          formik.setFieldValue('location.realAddress', data.display_name)
          const titleNow = (formik.values.location?.title || '').trim()
          if (!titleNow) {
            formik.setFieldValue('location.title', suggestedTitleFromAddress(data.display_name))
          }
        }
      }
    } catch (error) {
      console.error('Error fetching location details:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION' }, { defaultMessage: 'Location' })}</h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          <div className='mb-10'>
            <label className='form-label'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.TITLE' })}
            </label>
            <input
              type='text'
              className='form-control form-control-solid'
              name='location.title'
              value={formik.values.location?.title || ''}
              onChange={formik.handleChange}
              placeholder={intl.formatMessage(
                { id: 'TOUR.FIELDS.LOCATION.TITLE_PLACEHOLDER' },
                { defaultMessage: 'Enter location title' }
              )}
            />
          </div>

          <div className='mb-10'>
            <label className='form-label'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.ADDRESS' })}
            </label>
              <input
                type='text'
                className='form-control form-control-solid'
                name='location.realAddress'
                value={formik.values.location?.realAddress || ''}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e)
                  const addr = e.target.value.trim()
                  if (addr && !(formik.values.location?.title || '').trim()) {
                    formik.setFieldValue('location.title', suggestedTitleFromAddress(addr))
                  }
                }}
              />
              {isLoading && (
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
            </div>
              )}
          </div>

          <div className='mb-10'>
            <label className='form-label'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.MAP' }, { defaultMessage: 'Map' })}
            </label>
            <div className='small text-muted mb-2'>
              {intl.formatMessage(
                { id: 'TOUR.FIELDS.LOCATION.MAP_INSTRUCTIONS_CLICK' },
                { defaultMessage: 'Click on the map or drag the marker to set the location' }
              )}
            </div>
            
            {/* Map and coordinates container with debug controls */}
            <div className='mb-10 border rounded p-2'>
              <div className='row'>
                {/* Map takes up half the width */}
                <div className='col-md-6'>
                  <div 
                    ref={mapRef}
                    className='ratio ratio-4x3'
                    style={{ 
                      minHeight: '300px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                    }}
                    id="mapContainer"
                  />
                </div>
                
                {/* Coordinate inputs take up half the width and are stacked vertically */}
                <div className='col-md-6'>
                  <div className='mb-5'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.LATITUDE' }, { defaultMessage: 'Map Latitude' })}
                    </label>
                    <input
                      type='number'
                      className='form-control form-control-solid'
                      name='location.mapLatitude'
                      value={latitude}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        formik.setFieldValue('location.mapLatitude', value);
                      }}
                      step='0.000001'
                    />
                  </div>

                  <div className='mb-5'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.LONGITUDE' }, { defaultMessage: 'Map Longitude' })}
                    </label>
                    <input
                      type='number'
                      className='form-control form-control-solid'
                      name='location.mapLongitude'
                      value={longitude}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        formik.setFieldValue('location.mapLongitude', value);
                      }}
                      step='0.000001'
                    />
                  </div>

                  <div className='mb-5'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.ZOOM' }, { defaultMessage: 'Map Zoom' })}
                    </label>
                    <input
                      type='number'
                      className='form-control form-control-solid'
                      name='location.mapZoom'
                      value={mapZoom}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setMapZoom(value);
                        formik.setFieldValue('location.mapZoom', value);
                      }}
                      min='2'
                      max='18'
                    />
                  </div>

                  {/* Location Presets */}
                  <div className='mb-5'>
                    <label className='form-label'>
                      {intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION.PRESETS' }, { defaultMessage: 'Location Presets' })}
                    </label>
                    <div className='d-flex flex-wrap gap-2'>
                      {Object.entries(LOCATION_COORDINATES).map(([key, location]) => (
                        <button
                          key={key}
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => {
                            formik.setFieldValue('location.mapLatitude', location.lat);
                            formik.setFieldValue('location.mapLongitude', location.lng);
                            formik.setFieldValue('location.mapZoom', location.zoom);
                            formik.setFieldValue('location.title', location.name);
                            fetchLocationDetails(location.lat, location.lng);
                          }}
                        >
                          {location.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RouteWaypointsEditor formik={formik} intl={intl} />

      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>
              {intl.formatMessage({
                id: 'TOUR.LOCATION.SCHEDULE.TITLE',
                defaultMessage: 'Tour schedule (global)',
              })}
            </h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          <p className='text-muted small mb-5'>
            {intl.formatMessage({
              id: 'TOUR.LOCATION.SCHEDULE.HINT',
              defaultMessage:
                'These times are independent from itinerary elements and will be displayed under the map on the front office.',
            })}
          </p>
          <div className='row g-5'>
            <div className='col-md-6'>
              <label className='form-label'>
                {intl.formatMessage({ id: 'TOUR.FIELDS.START_TIME' })}
              </label>
              <input
                type='time'
                className='form-control form-control-solid'
                name='departureTime'
                value={formik.values.departureTime || ''}
                onChange={formik.handleChange}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>
                {intl.formatMessage({ id: 'TOUR.FIELDS.END_TIME' })}
              </label>
              <input
                type='time'
                className='form-control form-control-solid'
                name='arrivalTime'
                value={formik.values.arrivalTime || ''}
                onChange={formik.handleChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Itinerary Section */}
      <Itinerary 
        formik={formik}
        showForm={showItineraryForm}
        setShowForm={setShowItineraryForm}
        newItinerary={newItinerary}
        setNewItinerary={setNewItinerary}
        handleAddItinerary={handleAddItinerary}
        intl={intl}
        events={Array.isArray(tourEvents?.data) ? tourEvents.data : (Array.isArray(tourEvents) ? tourEvents : [])}
      />

      <p className='text-muted small mt-8 mb-0'>
        {intl.formatMessage({
          id: 'TOUR.LOCATION.SAVE_VIA_FOOTER',
          defaultMessage:
            'Use the blue Save button at the bottom of the page to store the map, route waypoints, and other tour changes.',
        })}
      </p>
    </div>
  )
}

// Add TypeScript definition for Leaflet
declare global {
  interface Window {
    L: any;
  }
}

export { LocationTab }